import { ReactElement, useEffect, useState } from 'react';
import { FieldProps, ErrorMessage, useFormikContext, getIn } from 'formik';
import { validateAbnAsync, validateAcnAsync } from '@api/index';
import { ABN_MIN_LENGTH, ACN_MIN_LENGTH } from '@constants/abnacn';

import classnames from 'classnames';
import styles from './ABNACNField.module.scss';

interface iProps extends FieldProps {
  title?: string;
  subtitle: string;
  isMandatory: boolean;
  placeholder: string;
  type?: string;
  disabled: boolean;
  autocomplete?: string;
  ABNACNType: string;
  ABNACNValidFieldName: string
}

const ABNACNField = ({ title = '',
  type = 'text',
  autocomplete = 'on',
  field, subtitle, isMandatory, placeholder, disabled, ABNACNType, ABNACNValidFieldName }: iProps): ReactElement => {
  const { errors, touched }: { errors: any; touched: any } = useFormikContext();
  // loading = true when the API call is running
  const [loading, setLoading] = useState(false);
  // API Call counter
  const [ABNCounter, setABNCounter] = useState(0);
  // value of the user input
  const [inputValue, setInputValue] = useState('');
  const hasError = getIn(errors, field.name);
  const touchedError = getIn(touched, field.name);
  const customisedErrorMessage = (errorMessage: string): ReactElement => {
    if (errorMessage.includes('<>')) {
      const removedChars = errorMessage.replace('<>', '');
      return (
        <span className={styles.errorMsg}>
          {removedChars}
          {' '}
          <i>{'<>'}</i>
        </span>
      );
    }
      return <span className={styles.errorMsg}>{errorMessage}</span>;
  };
  const formikContext = useFormikContext();

  useEffect(() => {
    let currentCall = ABNCounter;
    // API call to validate ABN
    const callABNAPI = async () => {
      setLoading(true);
      await validateAbnAsync(inputValue)
      .then((response) => {
        if (currentCall === ABNCounter) {
          if (response === '') {
            formikContext.setFieldTouched(field.name, true);
            formikContext.setFieldValue(ABNACNValidFieldName, true);
          } else {
            formikContext.setFieldValue(ABNACNValidFieldName, false);
          }
          setLoading(false);
        }
      });
    };
    const callACNAPI = async () => {
        setLoading(true);
        await validateAcnAsync(inputValue)
        .then((response) => {
          if (currentCall === ABNCounter) {
            if (response === '') {
              formikContext.setFieldValue(ABNACNValidFieldName, true);
            } else {
              formikContext.setFieldValue(ABNACNValidFieldName, false);
            }
            setLoading(false);
          }
        });
    };

    if (inputValue !== '') {
      if (ABNACNType === 'ABN' && inputValue.length >= ABN_MIN_LENGTH) callABNAPI();
      if (ABNACNType === 'ACN' && inputValue.length >= ACN_MIN_LENGTH) callACNAPI();
      if (loading) if (ABNACNType === 'ACN' && inputValue.length < ABN_MIN_LENGTH) setLoading(false);
      if (loading) if (ABNACNType === 'ACN' && inputValue.length < ABN_MIN_LENGTH) setLoading(false);
    }
    return () => {
        currentCall = 0;
    };
  }, [inputValue, ABNCounter]);

  const handleChange = (e: any) => {
    formikContext.handleChange(e);
    setInputValue(e.target.value);
    setABNCounter((prevCounter) => prevCounter + 1);
};

  return (
    <div
      className={classnames({
        [styles.container]: true,
        [styles.titlePresent]: title,
      })}>
      {title && (
        <div
          className={classnames({
            [styles.title]: true,
            [styles.titleError]: hasError && touchedError,
          })}>
          <span className={styles.titleBold}>{title}</span>
          {!isMandatory && <span className={styles.optional}> ( optional )</span>}
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      )}
      <div className={styles.inputContainer}>
        <input
          type={type}
          className={classnames({
            [styles.formInput]: true,
            [styles.inputError]: hasError && touchedError,
            [styles.disabled]: disabled,
          })}
          required={isMandatory}
          placeholder={placeholder}
          disabled={disabled}
          {...field}
          value={field.value ? field.value : ''}
        //   {...extraInputAttrs}
          onChange={handleChange}
          autoComplete={autocomplete}
          aria-label="Text Input"
        />
        {loading && (
          <div className={`${styles.cssloadcontainer} ${styles.inline}`}>
            <div className={styles.cssloadwhirlpool} />
          </div>
        )}
      </div>
      <ErrorMessage name={field.name} render={(msg) => customisedErrorMessage(msg)} />
    </div>
  );
};

export default ABNACNField;
